import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Printer,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  Zap,
  Sliders,
  Settings2,
  FileText,
  Palette,
  Sparkles,
  ExternalLink,
  Info,
  Laptop,
  Globe,
  HelpCircle,
  Play,
  Check,
  ChevronDown,
  ChevronUp,
  Cpu,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { createCanvas, canvasToDataURL } from "@/lib/imageEnhancer";

const VIRTUAL_PRINTER_PATTERN =
  /microsoft print to pdf|microsoft xps|fax|onenote|send to onenote|pdf24|cuteftp|dopdf|adobe pdf/i;

export default function PrinterManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Environment detection
  const isElectron = Boolean(window.jsmDesktop?.printers?.getAll);

  // Detected Windows printers from Electron IPC
  const [detectedPrinters, setDetectedPrinters] = useState([]);
  const [filterVirtual, setFilterVirtual] = useState(true);

  // Shop setup from backend
  const [shop, setShop] = useState(null);
  const [colorPrinter, setColorPrinter] = useState("");
  const [bwPrinter, setBwPrinter] = useState("");
  const [colorRate, setColorRate] = useState("20");
  const [bwRate, setBwRate] = useState("10");

  // Test print state
  const [testingPrinter, setTestingPrinter] = useState("");

  // Diagnostics panel toggle
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // -------------------------------------------------------------
  // Load Printers & Shop Config
  // -------------------------------------------------------------
  const loadPrintersAndSetup = useCallback(async () => {
    setLoading(true);
    let allDetected = [];

    // 1. Detect via Electron IPC
    if (window.jsmDesktop?.printers?.getAll) {
      try {
        const list = await window.jsmDesktop.printers.getAll();
        if (Array.isArray(list)) {
          allDetected = list.filter((p) => p.name);
          setDetectedPrinters(allDetected);
        }
      } catch (err) {
        console.warn("Printer discovery notice:", err);
      }
    }

    // 2. Load shop configuration from backend
    try {
      const res = await api.get("/scan-to-print/shop");
      if (res.data?.shop) {
        const s = res.data.shop;
        setShop(s);
        setColorRate(String(s.color_rate ?? 20));
        setBwRate(String(s.bw_rate ?? 10));

        const machines = s.machines || [];
        const colorM = machines.find((m) => m.machine_type === "color");
        const bwM = machines.find((m) => m.machine_type === "bw");

        if (colorM) setColorPrinter(colorM.device_name || colorM.machine_name);
        if (bwM) setBwPrinter(bwM.device_name || bwM.machine_name);
      }
    } catch (err) {
      console.warn("Could not load shop setup:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrintersAndSetup();
  }, [loadPrintersAndSetup]);

  // -------------------------------------------------------------
  // Refresh Printers
  // -------------------------------------------------------------
  const handleRefreshPrinters = async () => {
    setRefreshing(true);
    try {
      if (window.jsmDesktop?.printers?.getAll) {
        const list = await window.jsmDesktop.printers.getAll();
        if (Array.isArray(list)) {
          const valid = list.filter((p) => p.name);
          setDetectedPrinters(valid);
          toast.success(`Detected ${valid.length} Windows printer(s)`);
        }
      } else {
        toast.info("Browser Mode: Native Windows printer detection is available in PrintQ Desktop.");
      }
    } catch (err) {
      toast.error("Failed to detect Windows printers: " + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  // Filtered printer lists
  const physicalPrinters = useMemo(
    () => detectedPrinters.filter((p) => !VIRTUAL_PRINTER_PATTERN.test(p.name || "")),
    [detectedPrinters]
  );

  const displayedPrinters = filterVirtual ? physicalPrinters : detectedPrinters;
  const defaultPrinter = detectedPrinters.find((p) => p.isDefault);

  // -------------------------------------------------------------
  // Save Printer Configuration
  // -------------------------------------------------------------
  const handleSaveConfiguration = async () => {
    if (!bwPrinter && !colorPrinter) {
      toast.error("Please select at least one printer for B&W or Color printing.");
      return;
    }

    setSaving(true);
    try {
      const machines = [];

      if (colorPrinter) {
        const detected = detectedPrinters.find(
          (p) => (p.deviceName || p.name) === colorPrinter
        );
        machines.push({
          machine_name: detected?.displayName || colorPrinter,
          device_name: colorPrinter,
          machine_type: "color",
          enabled: true,
        });
      }

      if (bwPrinter) {
        const detected = detectedPrinters.find(
          (p) => (p.deviceName || p.name) === bwPrinter
        );
        machines.push({
          machine_name: detected?.displayName || bwPrinter,
          device_name: bwPrinter,
          machine_type: "bw",
          enabled: true,
        });
      }

      const payload = {
        color_rate: parseFloat(colorRate) || 20,
        bw_rate: parseFloat(bwRate) || 10,
        machines,
      };

      const res = await api.put("/scan-to-print/shop/setup", payload);
      if (res.data?.shop) {
        setShop(res.data.shop);
        toast.success("Printer configuration saved successfully!");
      }
    } catch (err) {
      toast.error("Failed to save printer setup: " + formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------
  // Generate & Dispatch Test Print (300 DPI A4)
  // -------------------------------------------------------------
  const handleTestPrint = async (targetPrinterName, isColor = false) => {
    if (!targetPrinterName) {
      toast.error("Please select a printer first.");
      return;
    }

    setTestingPrinter(targetPrinterName);

    try {
      // 1. Create high-resolution 300 DPI Test Page Canvas (2480 × 3508 px)
      const canvas = createCanvas(2480, 3508);
      const ctx = canvas.getContext("2d");

      // Pure white paper background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 2480, 3508);

      // Top Border Banner
      ctx.fillStyle = "#09090b";
      ctx.fillRect(0, 0, 2480, 320);

      // Header Branding
      ctx.fillStyle = "#f59e0b";
      ctx.font = "bold 96px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("PrintQ", 120, 190);

      ctx.fillStyle = "#ffffff";
      ctx.font = "52px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("— Scan. Enhance. Print.", 480, 190);

      ctx.fillStyle = "#a1a1aa";
      ctx.font = "36px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("JSM DIGITAL SOLUTIONS • Windows Test Print", 120, 260);

      // Printer Specs Card
      ctx.fillStyle = "#f8fafc";
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.roundRect(120, 420, 2240, 520, 24);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 60px 'Segoe UI', Arial, sans-serif";
      ctx.fillText(`Target Printer: ${targetPrinterName}`, 180, 520);

      ctx.fillStyle = "#475569";
      ctx.font = "40px 'Segoe UI', Arial, sans-serif";
      ctx.fillText(`Print Mode: ${isColor ? "Full Color HD" : "Black & White Xerox"}`, 180, 600);
      ctx.fillText(`Timestamp: ${new Date().toLocaleString()}`, 180, 670);
      ctx.fillText(`Driver Status: Spooler Connected ✓`, 180, 740);
      ctx.fillText(`Support: Kunal Warkante (+91 9545262332)`, 180, 810);

      // Color Swatches (CMYK & RGB)
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 44px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("Color & Density Calibration Grid", 120, 1060);

      const swatches = [
        { label: "Black 100%", color: "#000000" },
        { label: "Gray 75%", color: "#404040" },
        { label: "Gray 50%", color: "#808080" },
        { label: "Gray 25%", color: "#c0c0c0" },
        { label: "Cyan", color: "#00a0e9" },
        { label: "Magenta", color: "#e4007f" },
        { label: "Yellow", color: "#fff100" },
        { label: "PrintQ Gold", color: "#f59e0b" },
      ];

      const swW = 260;
      const swH = 180;
      swatches.forEach((sw, idx) => {
        const x = 120 + idx * 280;
        ctx.fillStyle = sw.color;
        ctx.fillRect(x, 1120, swW, swH);
        ctx.strokeStyle = "#94a3b8";
        ctx.strokeRect(x, 1120, swW, swH);

        ctx.fillStyle = "#334155";
        ctx.font = "bold 28px 'Segoe UI', Arial, sans-serif";
        ctx.fillText(sw.label, x + 10, 1340);
      });

      // Grayscale gradient strip
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 44px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("16-Step Grayscale Ramp", 120, 1460);

      const gradW = 2240 / 16;
      for (let g = 0; g < 16; g++) {
        const val = Math.round((g / 15) * 255);
        ctx.fillStyle = `rgb(${val}, ${val}, ${val})`;
        ctx.fillRect(120 + g * gradW, 1510, gradW, 120);
        ctx.strokeStyle = "#94a3b8";
        ctx.strokeRect(120 + g * gradW, 1510, gradW, 120);
      }

      // Micro-Text Sharpness Chart
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 44px 'Segoe UI', Arial, sans-serif";
      ctx.fillText("Text Readability & Sharpness Chart", 120, 1750);

      const fontSizes = [
        { pt: 36, text: "PrintQ 36pt: High Definition Document Scanning & Printing Engine" },
        { pt: 28, text: "PrintQ 28pt: Automatic 4-Corner Perspective Homography Deskew" },
        { pt: 22, text: "PrintQ 22pt: Magic Color CamScanner-Style Background Normalization" },
        { pt: 18, text: "PrintQ 18pt: Adaptive Local Sauvola Xerox Thresholding for Razor-Sharp Text" },
        { pt: 14, text: "PrintQ 14pt: Physical A4 (210×297mm) and CR80 ID Card (85.60×53.98mm) Precision" },
        { pt: 10, text: "PrintQ 10pt: Micro-Text Legibility Verification 1234567890 • JSM DIGITAL SOLUTIONS" },
      ];

      let curY = 1830;
      fontSizes.forEach((f) => {
        ctx.fillStyle = "#0f172a";
        ctx.font = `${f.pt * 2.5}px 'Segoe UI', Arial, sans-serif`;
        ctx.fillText(f.text, 120, curY);
        curY += f.pt * 3.5;
      });

      // Alignment Marks on 4 corners
      const tick = 60;
      ctx.strokeStyle = "#0f172a";
      ctx.lineWidth = 6;

      // TL
      ctx.beginPath();
      ctx.moveTo(60, 60); ctx.lineTo(60 + tick, 60);
      ctx.moveTo(60, 60); ctx.lineTo(60, 60 + tick);
      // TR
      ctx.moveTo(2420, 60); ctx.lineTo(2420 - tick, 60);
      ctx.moveTo(2420, 60); ctx.lineTo(2420, 60 + tick);
      // BL
      ctx.moveTo(60, 3448); ctx.lineTo(60 + tick, 3448);
      ctx.moveTo(60, 3448); ctx.lineTo(60, 3448 - tick);
      // BR
      ctx.moveTo(2420, 3448); ctx.lineTo(2420 - tick, 3448);
      ctx.moveTo(2420, 3448); ctx.lineTo(2420, 3448 - tick);
      ctx.stroke();

      // Footer
      ctx.fillStyle = "#64748b";
      ctx.font = "32px 'Segoe UI', Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("PrintQ • Powered by JSM DIGITAL SOLUTIONS • http://localhost:8000", 1240, 3400);

      const testDataUrl = canvasToDataURL(canvas, "image/jpeg", 0.95);

      // 2. Dispatch via Electron IPC (Direct Silent Print) or Browser Fallback
      if (window.jsmDesktop?.printers?.printDocument) {
        const result = await window.jsmDesktop.printers.printDocument({
          dataUrl: testDataUrl,
          printerName: targetPrinterName,
          copies: 1,
          color: isColor,
        });

        if (result?.success) {
          toast.success(`Test print sent to ${targetPrinterName}!`);
        } else {
          throw new Error(result?.error || "Printer spooler did not accept the job");
        }
      } else {
        // Web Browser Mode fallback
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>PrintQ — Test Print</title>
                <style>
                  @page { size: A4 portrait; margin: 0; }
                  body { margin: 0; padding: 0; background: #fff; display: flex; justify-content: center; align-items: center; }
                  img { width: 100vw; height: 100vh; object-fit: contain; }
                </style>
              </head>
              <body>
                <img src="${testDataUrl}" onload="window.print(); window.close();" />
              </body>
            </html>
          `);
          printWindow.document.close();
        }
        toast.success("Test print page opened in print dialog!");
      }
    } catch (err) {
      console.error("Test print error:", err);
      toast.error("Test print error: " + (err.message || "Failed to print"));
    } finally {
      setTestingPrinter("");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4 md:p-6 max-w-6xl mx-auto flex flex-col gap-6 select-none animate-fade-in">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-zinc-900/70 border border-zinc-800 rounded-2xl p-5 backdrop-blur-md shadow-xl">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Printer className="w-4 h-4" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-white flex items-center gap-1.5">
              <span>Print</span>
              <span className="text-amber-400">Q</span>
              <span className="text-zinc-400 font-medium text-lg ml-1">Printer Setup</span>
            </h1>
          </div>
          <p className="text-xs md:text-sm text-zinc-400 mt-1">
            Configure default Black & White and Color Windows printers for direct silent printing.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDiagnostics(!showDiagnostics)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold border border-zinc-700 transition-all active:scale-95"
          >
            <Cpu className="w-3.5 h-3.5 text-amber-400" />
            <span>Diagnostics</span>
            {showDiagnostics ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          <button
            type="button"
            onClick={handleRefreshPrinters}
            disabled={refreshing}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold border border-zinc-700 transition-all active:scale-95 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-amber-400" : ""}`} />
            <span>{refreshing ? "Detecting..." : "Refresh Printers"}</span>
          </button>
        </div>
      </div>

      {/* Environment Status Alert */}
      {isElectron ? (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center justify-between text-xs text-emerald-400">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center font-bold">
              ✓
            </div>
            <div>
              <strong className="text-white block font-bold">Electron Desktop Mode Active</strong>
              <span className="text-zinc-300">
                Native Windows Printer Spooler IPC connected. Direct silent printing is enabled.
              </span>
            </div>
          </div>
          <span className="hidden sm:inline-block font-mono text-[11px] bg-emerald-500/20 px-2 py-0.5 rounded-md font-bold">
            IPC READY
          </span>
        </div>
      ) : (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-amber-300">
          <div className="flex items-start gap-2.5">
            <Globe className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <strong className="text-white block font-bold">
                Browser Mode — Native Windows printer detection is available in PrintQ Desktop.
              </strong>
              <span className="text-zinc-400">
                When accessed through a web browser, PrintQ utilizes the system print dialog. Launch the PrintQ Desktop app for direct silent printing.
              </span>
            </div>
          </div>
          <span className="font-mono text-[10px] bg-amber-500/20 text-amber-400 px-2 py-1 rounded-md font-bold shrink-0">
            SYSTEM DIALOG MODE
          </span>
        </div>
      )}

      {/* Diagnostics Panel (Collapsible) */}
      {showDiagnostics && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 shadow-xl flex flex-col gap-4 text-xs animate-scale-in">
          <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-bold text-white">PrintQ Printer Diagnostics</h3>
            </div>
            <span className="text-[10px] text-zinc-500 font-mono">v1.0.0-PRO</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800/80 flex flex-col gap-1">
              <span className="text-zinc-500 text-[10px] uppercase font-bold">Application Runtime</span>
              <strong className="text-white font-mono text-xs flex items-center gap-1.5">
                {isElectron ? "Electron Desktop ✓" : "Web Browser"}
              </strong>
            </div>

            <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800/80 flex flex-col gap-1">
              <span className="text-zinc-500 text-[10px] uppercase font-bold">Printer IPC Bridge</span>
              <strong className={`font-mono text-xs ${isElectron ? "text-emerald-400" : "text-amber-400"}`}>
                {isElectron ? "window.jsmDesktop (Active)" : "Unavailable (Web)"}
              </strong>
            </div>

            <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800/80 flex flex-col gap-1">
              <span className="text-zinc-500 text-[10px] uppercase font-bold">Detected Printers</span>
              <strong className="text-white font-mono text-xs">
                {detectedPrinters.length} Total ({physicalPrinters.length} Physical)
              </strong>
            </div>

            <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800/80 flex flex-col gap-1">
              <span className="text-zinc-500 text-[10px] uppercase font-bold">Windows Default Device</span>
              <strong className="text-amber-400 font-mono text-xs truncate" title={defaultPrinter?.name || "None"}>
                {defaultPrinter?.name || "None detected"}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* Main Grid: Assignment Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ============================================================ */}
        {/* 1. BLACK & WHITE XEROX PRINTER CARD                          */}
        {/* ============================================================ */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between gap-5 shadow-lg relative overflow-hidden">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-zinc-950 border border-zinc-800 flex items-center justify-center text-zinc-300">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white flex items-center gap-1.5">
                    <span>B&W Xerox Printer</span>
                  </h2>
                  <p className="text-[11px] text-zinc-400">Used for single/double-sided B&W documents</p>
                </div>
              </div>

              {bwPrinter && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Assigned
                </span>
              )}
            </div>

            {/* Printer Selection Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-zinc-300">Select Windows Printer:</label>
              <select
                value={bwPrinter}
                onChange={(e) => setBwPrinter(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs outline-none focus:border-amber-500/60 font-medium"
              >
                <option value="">-- No B&W Printer Assigned --</option>
                {displayedPrinters.map((p) => (
                  <option key={p.name} value={p.deviceName || p.name}>
                    {p.displayName || p.name} {p.isDefault ? "★ (Windows Default)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Printing Rate per Page */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-zinc-300">B&W Rate per Page (₹):</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-bold">₹</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={bwRate}
                  onChange={(e) => setBwRate(e.target.value)}
                  className="w-full pl-8 pr-4 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs font-mono font-bold outline-none focus:border-amber-500/60"
                />
              </div>
            </div>
          </div>

          {/* Test Print Button */}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-800/80">
            <span className="text-[11px] text-zinc-500">A4 • 300 DPI Spooler</span>
            <button
              type="button"
              disabled={!bwPrinter || testingPrinter === bwPrinter}
              onClick={() => handleTestPrint(bwPrinter, false)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold text-xs transition-all active:scale-95 disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              <span>{testingPrinter === bwPrinter ? "Printing..." : "Test Print B&W"}</span>
            </button>
          </div>
        </div>

        {/* ============================================================ */}
        {/* 2. COLOR HD PRINTER CARD                                     */}
        {/* ============================================================ */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between gap-5 shadow-lg relative overflow-hidden">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                  <Palette className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white flex items-center gap-1.5">
                    <span>Color HD Printer</span>
                  </h2>
                  <p className="text-[11px] text-zinc-400">Used for high-resolution color photos & documents</p>
                </div>
              </div>

              {colorPrinter && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Assigned
                </span>
              )}
            </div>

            {/* Printer Selection Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-zinc-300">Select Windows Printer:</label>
              <select
                value={colorPrinter}
                onChange={(e) => setColorPrinter(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs outline-none focus:border-amber-500/60 font-medium"
              >
                <option value="">-- No Color Printer Assigned --</option>
                {displayedPrinters.map((p) => (
                  <option key={p.name} value={p.deviceName || p.name}>
                    {p.displayName || p.name} {p.isDefault ? "★ (Windows Default)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Printing Rate per Page */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-zinc-300">Color Rate per Page (₹):</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-bold">₹</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={colorRate}
                  onChange={(e) => setColorRate(e.target.value)}
                  className="w-full pl-8 pr-4 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs font-mono font-bold outline-none focus:border-amber-500/60"
                />
              </div>
            </div>
          </div>

          {/* Test Print Button */}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-800/80">
            <span className="text-[11px] text-zinc-500">A4 • CMYK HD Spooler</span>
            <button
              type="button"
              disabled={!colorPrinter || testingPrinter === colorPrinter}
              onClick={() => handleTestPrint(colorPrinter, true)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-amber-400 font-bold text-xs transition-all active:scale-95 disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              <span>{testingPrinter === colorPrinter ? "Printing..." : "Test Print Color"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Save Setup Action Bar */}
      <div className="flex items-center justify-end gap-3 bg-zinc-900/60 border border-zinc-800 p-4 rounded-2xl">
        <button
          type="button"
          disabled={saving}
          onClick={handleSaveConfiguration}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 active:scale-95 text-zinc-950 font-black text-xs shadow-lg shadow-amber-500/20 transition-all cursor-pointer disabled:opacity-50"
        >
          <Check className="w-4 h-4" />
          <span>{saving ? "Saving Setup..." : "Save Printer Setup"}</span>
        </button>
      </div>

      {/* ============================================================ */}
      {/* DETECTED WINDOWS PRINTERS LIST                                */}
      {/* ============================================================ */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 flex flex-col gap-5 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-zinc-800 pb-4">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Printer className="w-4 h-4 text-amber-400" />
              <span>
                {isElectron
                  ? `Detected Windows Printers (${displayedPrinters.length})`
                  : "Native Windows Printers (Available in Desktop App)"}
              </span>
            </h3>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              {isElectron
                ? "Physical and virtual print devices recognized by the Windows print spooler."
                : "Launch PrintQ Desktop on Windows to see and assign all local USB & Network printers."}
            </p>
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filterVirtual}
              onChange={(e) => setFilterVirtual(e.target.checked)}
              className="rounded border-zinc-700 text-amber-500 focus:ring-amber-500"
            />
            <span>Hide virtual devices (PDF, XPS, Fax)</span>
          </label>
        </div>

        {/* Printer List */}
        {loading ? (
          <div className="py-12 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
            <span>Scanning Windows Print Spooler...</span>
          </div>
        ) : displayedPrinters.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {displayedPrinters.map((p) => {
              const isBw = bwPrinter === (p.deviceName || p.name);
              const isColor = colorPrinter === (p.deviceName || p.name);

              return (
                <div
                  key={p.name}
                  className={`p-4 rounded-xl border transition-all flex flex-col justify-between gap-3 bg-zinc-950/70 ${
                    isBw || isColor
                      ? "border-amber-500/60 shadow-md shadow-amber-500/10"
                      : "border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <strong className="text-xs font-bold text-white truncate" title={p.displayName || p.name}>
                        {p.displayName || p.name}
                      </strong>

                      {p.isDefault && (
                        <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          DEFAULT
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-[10px] text-zinc-400 mt-1">
                      <span className="inline-flex items-center gap-1 text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {p.statusText || "Ready"}
                      </span>
                      <span>•</span>
                      <span>{p.isVirtual ? "Virtual" : "Physical Device"}</span>
                    </div>
                  </div>

                  {/* Quick Assign Buttons */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-800/80">
                    <button
                      type="button"
                      onClick={() => setBwPrinter(p.deviceName || p.name)}
                      className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                        isBw
                          ? "bg-zinc-800 text-white border border-zinc-600 font-extrabold"
                          : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"
                      }`}
                    >
                      {isBw ? "✓ B&W" : "Set B&W"}
                    </button>

                    <button
                      type="button"
                      onClick={() => setColorPrinter(p.deviceName || p.name)}
                      className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                        isColor
                          ? "bg-amber-500 text-zinc-950 font-black shadow-sm"
                          : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"
                      }`}
                    >
                      {isColor ? "✓ Color" : "Set Color"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleTestPrint(p.deviceName || p.name, false)}
                      className="px-2 py-1.5 rounded-lg bg-zinc-900 text-zinc-400 hover:text-amber-400 border border-zinc-800 text-[10px] font-bold"
                      title="Send Test Print"
                    >
                      Test
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-8 text-center flex flex-col items-center gap-3 text-zinc-400 text-xs">
            <Printer className="w-8 h-8 text-zinc-600" />
            <div>
              <strong className="text-white block">
                {isElectron ? "No Windows Printers Detected" : "Browser Mode Active"}
              </strong>
              <p className="text-zinc-500 mt-1 max-w-sm">
                {isElectron
                  ? "Make sure your printer is powered on and connected via USB or local network, then click 'Refresh Printers'."
                  : "Native printer detection runs in the PrintQ Windows Desktop App."}
              </p>
            </div>
            {isElectron && (
              <button
                type="button"
                onClick={handleRefreshPrinters}
                className="mt-1 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs transition-colors"
              >
                Refresh Printers
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
